class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('pointerdown', () => { this.keyboardInteraction = false; });
    this.addEventListener('keydown', () => { this.keyboardInteraction = true; });
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.keyboardInteraction = event.detail === 0;
      cartLink.classList.toggle('cart-pointer-focus', !this.keyboardInteraction);
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      this.keyboardInteraction = true;
      cartLink.classList.remove('cart-pointer-focus');
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty') && !this.isAdding
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    if (!this.isAdding) this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  renderPendingItem(item) {
    const body = this.querySelector('.cart-drawer__optimistic tbody');
    if (!body || !item) return;
    const existing = [...this.querySelectorAll('.cart-item:not(.cart-item--pending)')].filter((row) =>
      row.querySelector('[data-quantity-variant-id]')?.dataset.quantityVariantId === item.variantId &&
      row.dataset.cartCustomized === 'false'
    );
    const row = document.createElement('tr');
    row.className = 'cart-item cart-item--pending';
    row.innerHTML = '<td class="cart-item__media"></td><td class="cart-item__details"><span class="cart-item__name h4"></span><dl></dl></td><td class="cart-item__totals"><span class="price"></span></td><td class="cart-item__quantity"><div class="quantity cart-quantity"><span class="quantity__button" aria-hidden="true">−</span><input class="quantity__input" disabled><span class="quantity__button" aria-hidden="true">+</span></div></td>';
    let quantity = item.quantity;
    if (!item.customized && existing.length === 1) {
      this.pendingExistingRow = existing[0];
      this.pendingExistingDisplay = existing[0].style.display;
      quantity += Number(existing[0].querySelector('[data-quantity-variant-id]').value) || 0;
      existing[0].style.display = 'none';
    }
    row.querySelector('.cart-item__name').textContent = item.title;
    if (item.image) {
      const image = document.createElement('img');
      image.className = 'cart-item__image';
      image.src = item.image;
      image.alt = '';
      image.width = 150;
      row.querySelector('.cart-item__media').append(image);
    }
    for (const option of item.options) {
      if (option.value === 'Default Title') continue;
      const detail = document.createElement('div');
      detail.className = 'product-option';
      if (option.name === 'Size') detail.classList.add('product-option--size');
      const name = document.createElement('dt');
      name.textContent = option.name ? `${option.name}: ` : '';
      const value = document.createElement('dd');
      value.textContent = option.value;
      detail.append(name, value);
      row.querySelector('dl').append(detail);
    }
    row.querySelector('input').value = quantity;
    row.querySelector('.price').textContent = new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency', currency: item.currency, minimumFractionDigits: 0, maximumFractionDigits: 0,
    }).format(Math.round(item.price / 100));
    body.replaceChildren(row);
  }

  beginAdd(item) {
    this.confirmedCartDataPromise = null;
    this.renderPendingItem(item);
    this.isAdding = true;
    this.addWasOpen = this.classList.contains('active');
    this.addClosed = false;
    this.classList.add('is-loading');
    this.setAttribute('aria-busy', 'true');
    this.addInertElements = [...this.querySelectorAll('cart-drawer-items, .drawer__footer')]
      .map((element) => ({ element, inert: element.inert }));
    this.addInertElements.forEach(({ element }) => { element.inert = true; });
    this.addCheckout = this.querySelector('#CartDrawer-Checkout');
    this.addCheckoutDisabled = this.addCheckout?.disabled;
    this.addCheckoutText = this.addCheckout?.textContent;
    if (this.addCheckout) {
      this.addCheckout.disabled = true;
      if (this.classList.contains('is-empty')) this.addCheckout.textContent = this.addCheckout.dataset.checkoutLabel || this.addCheckout.textContent;
    }
    const items = this.querySelector('cart-drawer-items');
    this.addScrollTop = items?.scrollTop;
    if (items) items.scrollTop = items.scrollHeight;
    this.open();
  }

  endAdd(restoreScroll = false) {
    if (!this.isAdding) return;
    this.querySelector('.cart-drawer__optimistic tbody')?.replaceChildren();
    if (this.pendingExistingRow) this.pendingExistingRow.style.display = this.pendingExistingDisplay;
    this.pendingExistingRow = null;
    const items = this.querySelector('cart-drawer-items');
    if (items && restoreScroll) items.scrollTop = this.addScrollTop || 0;
    this.isAdding = false;
    this.classList.remove('is-loading');
    this.removeAttribute('aria-busy');
    this.addInertElements?.forEach(({ element, inert }) => { element.inert = inert; });
    if (this.addCheckout) {
      this.addCheckout.disabled = this.addCheckoutDisabled;
      this.addCheckout.textContent = this.addCheckoutText;
    }
    this.addInertElements = null;
    this.addCheckout = null;
  }

  cancelAdd() {
    if (!this.isAdding) return;
    const shouldClose = !this.addWasOpen;
    this.endAdd(true);
    if (shouldClose) this.close();
  }

  close() {
    if (this.isAdding) this.addClosed = true;
    this.classList.remove('active');
    if (this.activeElement?.id === 'cart-icon-bubble') {
      this.activeElement.classList.toggle('cart-pointer-focus', !this.keyboardInteraction);
    }
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  beginRecommendationAdd() {
    this.isAdding = true;
    this.classList.add('is-loading');
    this.setAttribute('aria-busy', 'true');
    this.recommendationLocks = [...this.querySelectorAll('#CartDrawer-Form, cart-promo-code, .drawer__footer')]
      .map(element => ({ element, inert: element.inert }));
    this.recommendationLocks.forEach(({ element }) => { element.inert = true; });
    const checkout = this.querySelector('#CartDrawer-Checkout');
    this.recommendationCheckoutDisabled = checkout?.disabled;
    if (checkout) checkout.disabled = true;
  }

  endRecommendationAdd(refreshFailed = false) {
    this.isAdding = false;
    this.classList.remove('is-loading');
    this.removeAttribute('aria-busy');
    this.recommendationLocks?.forEach(({ element, inert }) => { element.inert = inert; });
    this.recommendationLocks = null;
    const checkout = this.querySelector('#CartDrawer-Checkout');
    if (checkout) checkout.disabled = refreshFailed || this.recommendationCheckoutDisabled;
  }

  async renderRecommendationContents(parsedState, anchor) {
    // Keep recommendation DOM (options, button state and carousel position) intact.
    if (!parsedState.sections?.['cart-drawer'] || !parsedState.sections?.['cart-icon-bubble']) {
      const response = await fetch(`${routes.cart_url}?sections=cart-drawer,cart-icon-bubble`);
      if (!response.ok) throw new Error('Cart refresh unavailable');
      parsedState.sections = await response.json();
    }
    const source = this.getSectionDOM(parsedState.sections['cart-drawer'], '#CartDrawer');
    const selectors = ['#CartDrawer-CartItems', '.drawer__footer'];
    const replacements = selectors.map(selector => ({
      target: this.querySelector(selector), source: source?.querySelector(selector),
    }));
    if (replacements.some(pair => !pair.target || !pair.source)) throw new Error('Cart refresh unavailable');
    const items = this.querySelector('cart-drawer-items');
    const anchorTop = anchor.getBoundingClientRect().top;
    const viewPayload = source.querySelector('cart-drawer-items')?.getAttribute('view-event-payload');
    if (viewPayload != null) items.setAttribute('view-event-payload', viewPayload);
    replacements.forEach(pair => { pair.target.innerHTML = pair.source.innerHTML; });
    const count = parsedState.sections['cart-icon-bubble'];
    if (count) document.getElementById('cart-icon-bubble').innerHTML = this.getSectionInnerHTML(count);
    items.scrollTop += anchor.getBoundingClientRect().top - anchorTop;
    this.querySelector('#CartDrawer-LiveRegionText').textContent = anchor.querySelector('[data-added-label]')?.dataset.addedLabel || '';
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    const wasAdding = this.isAdding;
    const keepClosed = wasAdding && this.addClosed;
    this.endAdd();
    this.classList.remove('is-empty');
    this.productId = parsedState.id;
    if (!this.renderCartUpdates(parsedState)) {
      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
        if (!sectionElement) return;
        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      });
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    }

    if (keepClosed) return;
    if (this.classList.contains('active')) {
      trapFocus(document.getElementById('CartDrawer'), this.querySelector('.drawer__inner'));
      this.querySelector('cart-drawer-items')?.dispatchViewEvent();
    } else {
      this.open();
    }
  }

  renderCartUpdates(parsedState) {
    const source = this.getSectionDOM(parsedState.sections?.['cart-drawer'] || '', '#CartDrawer');
    const items = this.querySelector('cart-drawer-items');
    const sourceItems = source?.querySelector('cart-drawer-items');
    if (!items || !sourceItems || items.classList.contains('is-empty') || sourceItems.classList.contains('is-empty')) return false;
    const selectors = ['#CartDrawer-CartItems', '.drawer__footer'];
    const replacements = selectors.map(selector => ({ target: this.querySelector(selector), source: source.querySelector(selector) }));
    if (replacements.some(pair => !pair.target || !pair.source)) return false;
    const scrollTop = items.scrollTop;
    replacements.forEach(pair => { pair.target.innerHTML = pair.source.innerHTML; });
    items.lineItemStatusElement = this.querySelector('#CartDrawer-LineItemStatus');
    const payload = sourceItems.getAttribute('view-event-payload');
    if (payload != null) items.setAttribute('view-event-payload', payload);
    const currentRecommendations = this.querySelector('cart-complementary-products');
    const nextRecommendations = source.querySelector('cart-complementary-products');
    const productSet = element => {
      try { return [...new Set(JSON.parse(element?.dataset.productIds || '[]').map(String))].sort().join(','); }
      catch { return null; }
    };
    if (productSet(currentRecommendations) !== productSet(nextRecommendations)) {
      if (currentRecommendations && nextRecommendations) currentRecommendations.replaceWith(nextRecommendations);
      else if (currentRecommendations) currentRecommendations.remove();
      else if (nextRecommendations) items.append(nextRecommendations);
    }
    const count = parsedState.sections?.['cart-icon-bubble'];
    if (count) document.getElementById('cart-icon-bubble').innerHTML = this.getSectionInnerHTML(count);
    items.scrollTop = scrollTop;
    return true;
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  updateQuantity(...args) {
    const drawer = this.closest('cart-drawer');
    if (drawer.isAdding || drawer.isUpdatingQuantity || drawer.isUpdatingDiscount || drawer.discountRefreshFailed || this.quantityRefreshFailed) return;
    return super.updateQuantity(...args);
  }

  enableLoading(line) {
    const drawer = this.closest('cart-drawer');
    this.quantityDrawer = drawer;
    drawer.isUpdatingQuantity = true;
    drawer.classList.add('is-updating-quantity');
    this.setAttribute('aria-busy', 'true');
    this.quantityFocus = document.activeElement;
    const errors = this.querySelector('#CartDrawer-CartErrors');
    if (errors) errors.textContent = '';
    this.quantityLocks = [...drawer.querySelectorAll('#CartDrawer-CartItems, cart-complementary-products, cart-promo-code')]
      .map(element => ({ element, inert: element.inert }));
    this.quantityLocks.forEach(({ element }) => { element.inert = true; });
    const checkout = drawer.querySelector('#CartDrawer-Checkout');
    this.quantityCheckoutDisabled = checkout?.disabled;
    if (checkout) checkout.disabled = true;
    this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`)
      .forEach(spinner => spinner.classList.remove('hidden'));
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const drawer = this.quantityDrawer;
    drawer.isUpdatingQuantity = false;
    drawer.classList.remove('is-updating-quantity');
    this.removeAttribute('aria-busy');
    this.quantityLocks?.forEach(({ element, inert }) => { element.inert = inert; });
    const checkout = drawer.querySelector('#CartDrawer-Checkout');
    if (checkout) checkout.disabled = Boolean(drawer.classList.contains('is-empty') || this.quantityRefreshFailed || this.quantityCheckoutDisabled);
    drawer.querySelectorAll('.loading__spinner').forEach(spinner => spinner.classList.add('hidden'));
    if (this.quantityFocus?.isConnected && drawer.classList.contains('active') &&
        (document.activeElement === document.body || document.activeElement === this.quantityFocus)) {
      this.quantityFocus.focus({ preventScroll: true });
    }
    this.quantityLocks = null;
    this.quantityFocus = null;
    this.lineItemStatusElement.setAttribute('aria-hidden', true);
    const input = this.querySelector(`#Drawer-quantity-${line}`);
    if (input && !this.quantityRefreshFailed) {
      input.value = input.getAttribute('value');
      input.closest('cart-drawer-quantity')?.validateQtyRules();
    }
  }

  syncContent(target, source) {
    const syncNode = (current, next) => {
      if (current.nodeType !== next.nodeType || current.nodeName !== next.nodeName) {
        current.replaceWith(next.cloneNode(true));
        return;
      }
      if (current.nodeType === Node.TEXT_NODE || current.nodeType === Node.COMMENT_NODE) {
        if (current.nodeValue !== next.nodeValue) current.nodeValue = next.nodeValue;
        return;
      }
      if (current.nodeType !== Node.ELEMENT_NODE) return;
      for (const attribute of [...current.attributes]) {
        if (!next.hasAttribute(attribute.name)) current.removeAttribute(attribute.name);
      }
      for (const attribute of next.attributes) {
        if (current.getAttribute(attribute.name) !== attribute.value) current.setAttribute(attribute.name, attribute.value);
      }
      this.syncContent(current, next);
      if (current instanceof HTMLInputElement) current.value = next.value;
    };
    const currentNodes = [...target.childNodes];
    const nextNodes = [...source.childNodes];
    nextNodes.forEach((next, index) => {
      if (currentNodes[index]) syncNode(currentNodes[index], next);
      else target.append(next.cloneNode(true));
    });
    currentNodes.slice(nextNodes.length).forEach(node => node.remove());
  }

  renderDrawerQuantity(state, line) {
    const drawer = this.closest('cart-drawer');
    const source = drawer.getSectionDOM(state.sections?.['cart-drawer'] || '', '#CartDrawer');
    const count = drawer.getSectionDOM(state.sections?.['cart-icon-bubble'] || '');
    const selectors = ['#CartDrawer-CartItems', '.cart-drawer__footer', '.cart__ctas'];
    const updates = selectors.map(selector => ({ target: drawer.querySelector(selector), source: source?.querySelector(selector) }));
    if (!count || updates.some(pair => !pair.target || !pair.source)) {
      this.quantityRefreshFailed = true;
      drawer.quantityRefreshFailed = true;
      throw new Error('Cart refresh unavailable');
    }
    if (state.item_count === 0) return false;
    const scrollTop = this.scrollTop;
    const input = this.querySelector(`#Drawer-quantity-${line}`);
    const requested = Number(input?.value);
    const previousCount = this.querySelectorAll('#CartDrawer-CartItems .cart-item').length;
    updates.forEach(pair => this.syncContent(pair.target, pair.source));
    this.syncContent(document.getElementById('cart-icon-bubble'), count);
    const payload = source.querySelector('cart-drawer-items')?.getAttribute('view-event-payload');
    if (payload != null) this.setAttribute('view-event-payload', payload);
    this.querySelectorAll('cart-drawer-quantity').forEach(picker => picker.validateQtyRules());
    const confirmed = state.items[line - 1]?.quantity;
    const message = previousCount === state.items.length && requested !== confirmed
      ? window.cartStrings.quantityError.replace('[quantity]', confirmed) : '';
    this.updateLiveRegions(line, message);
    this.scrollTop = scrollTop;
    return true;
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class CartDrawerQuantity extends QuantityInput {
  onButtonClick(event) {
    if (event.currentTarget.name === 'minus' && Number(this.input.value) === 1) {
      event.preventDefault();
      if (this.dataset.canRemove === 'false') return;
      this.closest('cart-drawer-items').updateQuantity(this.input.dataset.index, 0, event);
      return;
    }
    super.onButtonClick(event);
    this.validateQtyRules();
  }

  validateQtyRules() {
    super.validateQtyRules();
    const removesItem = Number(this.input.value) === 1;
    const minus = this.querySelector('[name="minus"]');
    minus.setAttribute('aria-label', removesItem ? this.dataset.removeLabel : this.dataset.decreaseLabel);
    minus.disabled = (removesItem ? this.dataset.canRemove : this.dataset.canUpdate) === 'false';
    this.querySelector('[data-quantity-remove]').hidden = !removesItem;
    this.querySelector('[data-quantity-minus]').hidden = removesItem;
  }
}

customElements.define('cart-drawer-quantity', CartDrawerQuantity);
